import { FC, useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import TextField from '@components/TextField/TextField';
import Select from '@components/Select/Select';
import Checkbox from '@components/Checkbox/Checkbox';
import Error from '@components/InputError/InputError';
import testId from '@functions/testId';
/*DEBT: Makes experience appear slower, remove this and related states (verifying, setVerifying)
and dependencies (in onSubmitRecaptchaVerification*/
import LoadingSpinner from '@components/LoadingSpinner/LoadingSpinner';
import formSources from '@functions/formSources/formSources';
import countries from '@variables/countries';
import gdprCountries from '@variables/gdprCountries';
import { omni_id } from '@variables/zapierWebhookIds';
//DEBT: Refactor to remove need for (verifying, setVerifying)
import { email, excludePeriods } from '@variables/regexExpressions';
import PrivacyPolicyNotice from '@components/PrivacyPolicyNotice/PrivacyPolicyNotice';
import submissionData from '@functions/submissionData/submissionData';
import submitToZapier from '@functions/submitToZapier';
import onSubmitRecaptchaVerification from '@functions/onSubmitRecaptchaVerification/onSubmitRecaptchaVerification';
import PrivacyPolicyCheckboxWrapper from '@components/PrivacyPolicyCheckboxWrapper/PrivacyPolicyCheckboxWrapper';
import GDPRNotice from '@components/GDPRNotice/GDPRNotice';

declare let sw;

const CrossSellForm: FC = () => {
    const [submitted, setSubmitted] = useState(false);

    const [verifying, setVerifying] = useState(false);

    const [gdpr, setGDPR] = useState(false);

    const [recaptchaError, setRecaptchaError] = useState(false);

    useEffect(() => {
        if (submitted === true) {
            window.location.href = '/thank-you/';
        }
    }, [submitted]);

    const {
        handleSubmit,
        register,
        formState: { errors },
        control,
        setValue,
    } = useForm({ mode: 'onChange' });

    const urlParams = new URLSearchParams(window.location.search);

    const sourcingInfo = formSources({
        secondarySource: 'D - Get Started',
        pageSource: window.formInfo,
        urlParams,
        paramKeys: [
            { urlKey: 'subid', salesforceKey: 'aclid' },
            'aclid',
            'transaction_id',
            'utm_source',
            'utm_medium',
            'utm_campaign',
            'utm_content',
            'utm_term',
            'page_url',
        ],
    });

    const onCountry = (e) => {
        const selected = e.value;
        if (gdprCountries.includes(selected)) {
            setGDPR(true);
        } else {
            setGDPR(false);
        }
        setValue('cs_country', e);
    };

    const onSubmit = async (data) => {
        if (typeof sw !== 'undefined' && window['environment']['name'] === 'production') {
            sw('send-form-submit', { email: data.cs_email });
        }

        data.form_name = 'Cross Sell Form';

        const combinedData = { ...submissionData({ data, prefix: 'cs' }), ...sourcingInfo };

        const submissionFunction = () =>
            submitToZapier({
                zapierId: omni_id,
                data: combinedData,
                setSubmitted: setSubmitted,
            });

        await onSubmitRecaptchaVerification({ setVerifying, setRecaptchaError, successFunction: submissionFunction });
    };

    return (
        <form
            className="form--cross-sell form--touchbistro-form"
            data-testid={testId('form--cross-sell')}
            onSubmit={handleSubmit(onSubmit)}
        >
            <LoadingSpinner verifying={verifying} background={true} />
            <div className="row">
                <div className="col col-12 col-lg-6 position-relative mb-3">
                    <TextField
                        type={'text'}
                        placeholder={'First Name'}
                        label={'First Name'}
                        testId={'cs-first-name'}
                        register={{
                            ...register('cs_first_name', {
                                required: true,
                                minLength: 1,
                                pattern: {
                                    value: excludePeriods,
                                    message: 'Invalid First Name',
                                },
                            }),
                        }}
                    />
                    {errors.cs_first_name && <Error>{`${errors.cs_first_name.message}`}</Error>}
                </div>
                <div className="col col-12 col-lg-6 position-relative mb-3">
                    <TextField
                        type={'text'}
                        placeholder={'Last Name'}
                        label={'Last Name'}
                        testId={'cs-last-name'}
                        register={{
                            ...register('cs_last_name', {
                                required: true,
                                minLength: 1,
                            }),
                        }}
                    />
                    {errors.cs_last_name && <Error>{`${errors.cs_last_name.message}`}</Error>}
                </div>
                <div className="col col-12 col-lg-6 position-relative mb-3">
                    <TextField
                        type={'email'}
                        placeholder={'Email'}
                        label={'Email'}
                        testId={'cs-email'}
                        register={{
                            ...register('cs_email', {
                                required: true,
                                pattern: {
                                    value: email,
                                    message: 'Invalid Email Address',
                                },
                            }),
                        }}
                    />
                    {errors.cs_email && <Error />}
                </div>
                <div className="col col-12 col-lg-6 position-relative mb-3">
                    <TextField
                        type={'text'}
                        placeholder={'Company'}
                        label={'Company'}
                        testId={'cs-company'}
                        register={{
                            ...register('cs_company', {
                                required: true,
                                minLength: 1,
                            }),
                        }}
                    />
                    {errors.cs_company && <Error />}
                </div>
                <div className="col col-12 col-lg-6 position-relative mb-3">
                    <Controller
                        name="cs_country"
                        control={control}
                        rules={{ required: false }}
                        render={({ field }) => {
                            const { ref, ...relevantField } = field;
                            return (
                                <Select
                                    label={'Country'}
                                    placeholder={'Country'}
                                    testId={'cs-country'}
                                    menuPlacement={'top'}
                                    {...relevantField}
                                    onChange={onCountry}
                                    options={countries.map((country) => ({
                                        value: country,
                                        label: country,
                                    }))}
                                />
                            );
                        }}
                    />
                    {errors.cs_country && <Error />}
                </div>
                <div className="col col-12 position-relative mb-3">
                    <PrivacyPolicyCheckboxWrapper>
                        <Controller
                            name="cs_marketing_optin"
                            control={control}
                            defaultValue={false}
                            rules={{ required: false }}
                            render={({ field }) => (
                                <Checkbox
                                    name={field.name}
                                    value={field.value}
                                    testId={'cs-marketing-optin'}
                                    text={<PrivacyPolicyNotice />}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                />
                            )}
                        />
                    </PrivacyPolicyCheckboxWrapper>
                </div>
                {gdpr && (
                    <div className="col col-12 position-relative mb-3">
                        <Controller
                            name="cs_gdpr_processing_optin"
                            control={control}
                            defaultValue={false}
                            rules={{ required: gdpr }}
                            render={({ field }) => (
                                <Checkbox
                                    name={field.name}
                                    value={field.value}
                                    text={<GDPRNotice />}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                />
                            )}
                        />
                    </div>
                )}
            </div>
            <div className="row">
                <div className="col col-12 mt-5 text-center">
                    <input className="button" type="submit" value="Submit" data-testid={testId('cs-submit')} />
                    {recaptchaError && (
                        <p className="col col-12 recaptcha-error paragraph-xsmall coral mt-2">Recaptcha failed.</p>
                    )}
                </div>
            </div>
        </form>
    );
};

export default CrossSellForm;
